import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    Container,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Typography,
    Grid,
    Paper
} from '@mui/material';
import { useLocation } from 'react-router-dom';
import { ToastContainer, toast, Bounce } from 'react-toastify';
import { getData, postData, putData } from '../utils/helper';
import { PropagateLoader } from "react-spinners";
import { set } from 'date-fns';
import { useNavigate } from 'react-router-dom';


const CSSProperties = {
    display: "block",
    margin: "0 auto",
    borderColor: "red",
};
const SalaryForm = () => {
    const navigate = useNavigate();
    const [employees, setEmployees] = useState([]);
    let [loading, setLoading] = useState(false);
    const months = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
    ];
    const rowData = useLocation().state?.editData || {};
    const [isEditMode] = useState(!!rowData.id);

    const [formData, setFormData] = useState({
        employee_id: '',
        month: '',
        grade: '',
        basicSalary: '',
        incentive: '',
        qr_bonus: '',
        deductions: '',
        total: '',
        status: 'Pending'
    });

    const handleChange = (e) => {
        const { name, value } = e.target;
        if (name === 'employee_id') {
            const selectedEmployee = employees.find(emp => emp.id === value);
            setFormData(prev => ({
                ...prev,
                [name]: value,
                grade: selectedEmployee ? selectedEmployee.grade : '',
                basic_salary: selectedEmployee ? selectedEmployee.basic_salary : ''
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: value,
            }));
        }
    };

    const handleSubmit = async () => {
        setLoading(true);
        if (formData.employee === '' || formData.month === '' || formData.basicSalary === '') {
            setLoading(false);
            toast.error('Please fill all required fields', {
                position: "top-center",
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: false,
                pauseOnHover: true,
                draggable: true,
                progress: undefined,
                theme: "light",
                transition: Bounce,
            });
        } else {
            let response;
            if (isEditMode) {
                response = await putData('salaries/' + rowData.id, formData);
            } else {
                response = await postData('salaries', formData);
            }
            if (response.status === 401) {
                navigate('/', { state: { message: "Session Expired" } });
                setLoading(false);
                return;
            }

            setLoading(false);
            if (response.status === undefined) {
                toast.success('Data Save Successfully', {
                    position: "top-center",
                    autoClose: 3000,
                    hideProgressBar: false,
                    closeOnClick: false,
                    pauseOnHover: true,
                    draggable: true,
                    progress: undefined,
                    theme: "light",
                    transition: Bounce,
                });
                setFormData({
                    employee_id: '',
                    month: '',
                    grade: '',
                    basicSalary: '',
                    incentive: '',
                    qr_bonus: '',
                    deductions: '',
                    total: '',
                    status: 'Pending'
                });
            } else {
                toast.error(JSON.stringify(response?.response?.data?.errors), {
                    position: "top-center",
                    autoClose: 3000,
                    hideProgressBar: false,
                    closeOnClick: false,
                    pauseOnHover: true,
                    draggable: true,
                    progress: undefined,
                    theme: "light",
                    transition: Bounce,
                });
            }
        }

    };

    useEffect(() => {
        if (rowData) {
            setFormData({
                employee_id: rowData.employee_id || '',
                month: rowData.month || '',
                grade: rowData.grade || '',
                basic_salary: rowData.basic_salary || '',
                incentive: rowData.incentive || '',
                qr_bonus: rowData.qr_bonus || '',
                deductions: rowData.deductions || '',
                total: rowData.total || '',
                status: rowData.status || 'Pending'
            });
        }
    }, []);


    useEffect(() => {
        setLoading(true);
        const fetchEmployees = async () => {
            const response = await getData('employees');
            if (response.status === 401) {
                navigate('/', { state: { message: "Session Expired" } });
                setLoading(false);
                return;
            }
            setEmployees(response?.data);
            setLoading(false);
        }
        fetchEmployees();
    }, []);

    useEffect(() => {
        setFormData(prev => ({
            ...prev,
            total: (parseFloat(prev.basic_salary) || 0) + (parseFloat(prev.incentive) || 0) + (parseFloat(prev.qr_bonus) || 0) - (parseFloat(prev.deductions) || 0)
        }));
    }, [formData.basic_salary, formData.incentive, formData.qr_bonus, formData.deductions]);
    return (
        <>
            {loading && (
                <div style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '100vh',
                    width: '100vw',
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.8)', // Optional: semi-transparent white background
                    zIndex: 9999, // Ensure it's above other elements
                }}>
                    <PropagateLoader
                        color="#00BFFF"
                        loading={loading}
                        cssOverride={CSSProperties}
                        aria-label="Loading Spinner"
                        data-testid="loader"
                    />
                </div>
            )}
            <Container maxWidth="md" sx={{ mt: 4 }}>
                <Paper elevation={3} sx={{ p: 4 }}>
                    <Typography variant="h4" component="h1" gutterBottom align="center">
                        Salary Form
                    </Typography>
                    <ToastContainer />
                    <Box sx={{ mt: 3 }}>
                        <Grid container spacing={3}>
                            {/* Employee Dropdown */}
                            <Grid item size={12}>
                                <FormControl fullWidth>
                                    <InputLabel id="employee-label">Employee Name</InputLabel>
                                    <Select
                                        labelId="employee-label"
                                        id="employee_id"
                                        name="employee_id"
                                        value={formData.employee_id}
                                        label="Employee Name"
                                        onChange={handleChange}
                                        disabled={isEditMode}
                                        required
                                    >
                                        {employees.map((employee) => (
                                            <MenuItem key={employee.id} value={employee.id}>
                                                {employee.name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>

                            {/* Month Dropdown */}
                            <Grid item size={6}>
                                <FormControl fullWidth>
                                    <InputLabel id="month-label">Month</InputLabel>
                                    <Select
                                        labelId="month-label"
                                        id="month"
                                        name="month"
                                        value={formData.month}
                                        label="Month"
                                        onChange={handleChange}
                                        disabled={isEditMode}
                                        required
                                    >
                                        {months.map((month, index) => (
                                            <MenuItem key={index} value={month}>
                                                {month}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>

                            {/* Grade (auto-filled but editable) */}
                            <Grid item size={6}>
                                <TextField
                                    fullWidth
                                    id="grade"
                                    name="grade"
                                    label="Grade"
                                    value={formData.grade}
                                    onChange={handleChange}
                                    InputProps={{
                                        readOnly: true // Set to true if grade should not be editable
                                    }}
                                />
                            </Grid>

                            {/* Salary Components */}
                            <Grid item size={6}>
                                <TextField
                                    fullWidth
                                    id="basic_salary"
                                    name="basic_salary"
                                    label="Basic Salary"
                                    value={formData.basic_salary}
                                    onChange={handleChange}
                                    type="number"
                                    inputProps={{
                                        min: "0",
                                        step: "1"
                                    }}
                                    InputProps={{
                                        startAdornment: <Typography sx={{ mr: 1 }}>Rs</Typography>,
                                    }}
                                />
                            </Grid>

                            <Grid item size={6}>
                                <TextField
                                    fullWidth
                                    id="incentive"
                                    name="incentive"
                                    label="Monthly Incentive"
                                    value={formData.incentive}
                                    onChange={handleChange}
                                    type="number"
                                    inputProps={{
                                        min: "0",
                                        step: "1"
                                    }}
                                    InputProps={{
                                        startAdornment: <Typography sx={{ mr: 1 }}>Rs</Typography>,
                                    }}
                                />
                            </Grid>

                            <Grid item size={6}>
                                <TextField
                                    fullWidth
                                    id="qr_bonus"
                                    name="qr_bonus"
                                    label="QR Bonus"
                                    value={formData.qr_bonus}
                                    onChange={handleChange}
                                    type="number"
                                    inputProps={{
                                        min: "0",
                                        step: "1"
                                    }}
                                    InputProps={{
                                        startAdornment: <Typography sx={{ mr: 1 }}>Rs</Typography>,
                                    }}
                                />
                            </Grid>

                            <Grid item size={6}>
                                <TextField
                                    fullWidth
                                    id="deductions"
                                    name="deductions"
                                    label="Deductions"
                                    value={formData.deductions}
                                    onChange={handleChange}
                                    type="number"
                                    inputProps={{
                                        min: "0",
                                        step: "1"
                                    }}
                                    InputProps={{
                                        startAdornment: <Typography sx={{ mr: 1 }}>Rs</Typography>,
                                    }}
                                />
                            </Grid>

                            <Grid item size={12}>
                                <TextField
                                    fullWidth
                                    id="total"
                                    name="total"
                                    label="Total Salary"
                                    value={formData.total}
                                    onChange={handleChange}
                                    placeholder='Total Salary will be calculated automatically'
                                    type="number"
                                    inputProps={{
                                        min: "0",
                                        step: "1"
                                    }}
                                    InputProps={{
                                        startAdornment: <Typography sx={{ mr: 1 }}>Rs</Typography>,
                                    }}
                                />
                            </Grid>

                            {/* Status Field */}
                            <Grid item size={12}>
                                <FormControl fullWidth>
                                    <InputLabel id="status-label">Status *</InputLabel>
                                    <Select
                                        labelId="status-label"
                                        id="status"
                                        name="status"
                                        value={formData.status}
                                        label="Status *"
                                        onChange={handleChange}
                                        required
                                    >
                                        <MenuItem value="Pending">Pending</MenuItem>
                                        <MenuItem value="Paid">Paid</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>

                            {/* Submit Button */}
                            <Grid item size={12}>
                                <Button
                                    type="submit"
                                    variant="contained"
                                    onClick={handleSubmit}
                                    sx={{
                                        backgroundColor: '#00BFFF',
                                        color: 'white',
                                        '&:hover': {
                                            backgroundColor: '#009ACD', // darker shade for hover
                                        }
                                    }}
                                    fullWidth
                                    size="large"
                                >
                                    Submit
                                </Button>
                            </Grid>
                        </Grid>
                    </Box>
                </Paper>
            </Container>
        </>
    );
};

export default SalaryForm;